import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  applyAuthActionCode,
  auth,
  checkAuthActionCode,
  mergeConfirmedAuthEmail,
  reloadCurrentAuthUser,
} from '../utils/firebase';
import { capturePostHogEvent } from '../utils/posthogClient';
import {
  classifyContinueUrl,
  codeMatchesSignedInEmail,
  emailChangeSignInAddress,
  isApplicableEmailAction,
  isEmailChangeAction,
  mapAuthActionError,
  parseAuthActionQuery,
} from '../utils/authActionUrl';
import AuthActionContinue from './auth/AuthActionContinue';
import AuthActionExpired from './auth/AuthActionExpired';
import AuthActionStatus from './auth/AuthActionStatus';

const VERIFY_MODES = ['verifyEmail', 'verifyAndChangeEmail'];
const EMAIL_CHANGE_SIGN_IN_COPY = 'Return to the app and sign in with this address.';

function captureOutcome(mode, result) {
  capturePostHogEvent('auth-action-apply-code', { mode, result });
}

async function confirmMatchingAccount(signedInUser) {
  const addressBefore = signedInUser.email;
  const refreshed = await reloadCurrentAuthUser();
  const addressChanged = Boolean(
    refreshed
    && refreshed.uid === signedInUser.uid
    && refreshed.email
    && refreshed.email.trim().toLowerCase() !== (addressBefore || '').trim().toLowerCase(),
  );
  if (addressChanged) {
    await mergeConfirmedAuthEmail(refreshed.uid, refreshed.email);
  }
}

async function resolveEmailAction({ mode, oobCode, continueTarget }) {
  const actionInfo = await checkAuthActionCode(oobCode);
  const signedInUser = auth.currentUser;
  const sameAccount = Boolean(
    signedInUser && codeMatchesSignedInEmail(signedInUser.email, actionInfo),
  );
  const differentAccount = Boolean(signedInUser) && !sameAccount;
  if (!isApplicableEmailAction(actionInfo)) {
    captureOutcome(mode, 'error');
    return { status: 'unusable', continueTarget };
  }
  const emailChange = isEmailChangeAction(mode, actionInfo);
  await applyAuthActionCode(oobCode);
  if (sameAccount) {
    try {
      await confirmMatchingAccount(signedInUser);
    } catch {
      // The code is already applied. A later refresh or email merge stays best-effort.
    }
  }
  captureOutcome(mode, 'confirmed');
  return {
    status: differentAccount ? 'other-account' : 'confirmed',
    continueTarget: differentAccount ? { kind: 'app-return' } : continueTarget,
    emailChange,
    newAddress: emailChange ? emailChangeSignInAddress(actionInfo) : '',
  };
}

async function resolveExpiredCode(mode, continueTarget) {
  const signedInUser = auth.currentUser;
  if (signedInUser) {
    try {
      const refreshed = await reloadCurrentAuthUser();
      if (refreshed?.emailVerified) {
        captureOutcome(mode, 'confirmed');
        return { status: 'already', continueTarget };
      }
    } catch {
      // A failed reload cannot show a confirmation this page did not see.
    }
  }
  captureOutcome(mode, 'expired');
  return { status: 'expired', continueTarget };
}

function AuthActionPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [view, setView] = useState({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    const params = parseAuthActionQuery(location.search);
    if (params.mode === 'resetPassword') {
      navigate(`/reset-password${location.search}`, { replace: true });
      return undefined;
    }

    const continueTarget = classifyContinueUrl(
      params.continueUrl,
      window.location.origin,
    );

    const applyLink = async () => {
      if (!VERIFY_MODES.includes(params.mode) || !params.oobCode) {
        captureOutcome(params.mode || 'unknown', 'error');
        if (!cancelled) {
          setView({ status: 'unusable' });
        }
        return;
      }
      try {
        const nextView = await resolveEmailAction({
          mode: params.mode,
          oobCode: params.oobCode,
          continueTarget,
        });
        if (!cancelled) {
          setView(nextView);
        }
      } catch (error) {
        const mapped = mapAuthActionError(error?.code);
        const nextView = mapped === 'expired'
          ? await resolveExpiredCode(params.mode, continueTarget)
          : { status: 'unusable', continueTarget };
        if (mapped !== 'expired') {
          captureOutcome(params.mode, 'error');
        }
        if (!cancelled) {
          setView(nextView);
        }
      }
    };

    applyLink();
    return () => {
      cancelled = true;
    };
  }, [location.search, navigate]);

  if (view.status === 'loading') {
    return (
      <AuthActionStatus
        heading="Checking this link"
        body="One moment."
      />
    );
  }
  if (view.status === 'expired') {
    return <AuthActionExpired />;
  }
  if (view.status === 'already') {
    return (
      <AuthActionStatus
        heading="You’re already confirmed 🍻"
        body="Everything’s set. Let’s get you back to it."
        footer={<AuthActionContinue continueTarget={view.continueTarget} />}
      />
    );
  }
  if (view.status === 'other-account') {
    const note = view.emailChange ? EMAIL_CHANGE_SIGN_IN_COPY : '';
    const detail = view.emailChange ? view.newAddress : '';
    return (
      <AuthActionStatus
        heading="Email confirmed ✓"
        body="This link confirmed the address on the code. The account signed in on this browser was not changed."
        note={note}
        detail={detail}
        footer={<AuthActionContinue continueTarget={view.continueTarget} />}
      />
    );
  }
  if (view.status === 'confirmed') {
    const body = view.emailChange ? EMAIL_CHANGE_SIGN_IN_COPY : 'You’re all set.';
    const detail = view.emailChange ? view.newAddress : '';
    return (
      <AuthActionStatus
        heading="Email confirmed ✓"
        body={body}
        detail={detail}
        footer={<AuthActionContinue continueTarget={view.continueTarget} />}
      />
    );
  }
  return (
    <AuthActionStatus
      heading="This link can’t be used."
      body="Open the latest confirmation email and try that link."
    />
  );
}

export default AuthActionPage;
